/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import { action, computed, observable, when } from 'mobx';
import Fuse from 'fuse.js';
import merge from 'deepmerge';
import flatMap from 'lodash/flatMap';
import { LearningPath } from 'types';
import {
  getLearningPaths,
  joinLearningPath,
  unjoinLearningPath,
  deleteCustomSkillPath,
} from 'services/LearningPathService';

import { getLearningPathsForOrgLeader } from 'services/ManagementReportingService';
import FiltersStore, { FilterState } from 'stores/common/Filters';
import UserStore from 'stores/User';
import { getFilterConfigurations } from './config';

type FilterKeys =
  | 'pathTypes'
  | 'categories'
  | 'products'
  | 'roles'
  | 'taxonomyProducts'
  | 'taxonomyAudiences'
  | 'taxonomyTopics'
  | 'levels';

type LearningPathFilters = {
  [K in FilterKeys]?: FilterState[];
};

class LearningPathsStore extends FiltersStore<LearningPath> {
  rootStore: any;

  @observable learningPaths: Array<LearningPath> = [];

  @observable searchResults: Array<LearningPath> | null = null;

  @observable _filteredEntries: LearningPath[] = [];

  @observable.ref courseCollateral = [];

  @observable _currentPage = 1;

  @observable entriesPerPage = 10;

  @observable loading = false;

  @observable loaded = false;

  @observable deletePathModal = {
    show: false,
    mode: 'confirm',
    isDeleting: false,
  };

  constructor(rootStore: any) {
    super(
      {
        categories: [],
        products: [],
        roles: [],
        pathTypes: [],
        // new taxonomies:
        taxonomyProducts: [],
        taxonomyAudiences: [],
        taxonomyTopics: [],
        levels: [],
      } as LearningPathFilters,
      rootStore.routerStore,
    );
    this.rootStore = rootStore;
  }

  @action async init(userStore: UserStore, force = false) {
    when(
      () =>
        userStore.didFetchSubscription &&
        this.rootStore.featuresStore.allFeaturesFetched,
      async () => {
        if (userStore.hasActiveSubscription) {
          await this.getLearningPaths(force);
        } else if (userStore.isOrganizationLeader) {
          await this.getLearningPathsForOrgLeaders(force);
        } else {
          // Learning paths are a subscription/org-leader feature: this user has none to fetch,
          // but `loaded` still needs to resolve so callers waiting on it aren't stuck forever.
          this.loaded = true;
        }
      },
    );
  }

  getFilterConfigs = () =>
    getFilterConfigurations(this.rootStore.uiStore?.tFunction);

  @action searchEntries = (string: string) => {
    const options = {
      shouldSort: true,
      threshold: 0,
      tokenize: true,
      matchAllTokens: true,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: [
        'title',
        'description.body',
        'prerequisites.body',
        'lp_elements.code',
        'lp_elements.title',
        'code',
        'categories',
      ],
    };

    if (!string.length) {
      this.searchResults = this.filteredEntries;
      return;
    }

    const fuse = new Fuse(this.filteredEntries, options);
    this.searchResults = fuse.search(string);
  };

  @computed get query() {
    return this.rootStore.searchStore.query;
  }

  @computed get totalPages(): number {
    return Math.ceil(this.filteredEntries.length / this.entriesPerPage) || 1;
  }

  @computed get filteredCount(): number {
    return this.filteredEntries.length;
  }

  @computed get currentPage(): number {
    if (this._currentPage > this.totalPages) {
      return 1;
    }

    return this._currentPage;
  }

  @computed get entries(): Array<LearningPath> {
    // Return a unique array of learning paths entries, preferring the one with
    // the latest user activity
    let uniqueArray: Array<LearningPath> = Object.values(
      this.learningPaths.reduce((array, entry) => {
        if (
          !array[entry.last_user_activity] ||
          +entry.last_user_activity > +array[entry.code].last_user_activity
        ) {
          array[entry.code] = entry;
        }

        return array;
      }, {}),
    );

    uniqueArray = uniqueArray.map(
      (entry: LearningPath) =>
        merge(entry, {
          translations: [
            {
              language: 'en-US',
              title: entry.title,
            },
          ],
        }) as LearningPath,
    );

    return uniqueArray;
  }

  set entries(entries: Array<LearningPath>) {
    this.learningPaths = entries;
  }

  @computed get filteredEntries() {
    const checkedFiltersLength = flatMap(
      this.filterKeys.map((key) =>
        this.filters[key].filter((filter) => filter.checked),
      ),
    ).length;

    return checkedFiltersLength || this.query
      ? this._filteredEntries
      : this.entries;
  }

  @computed get paginatedEntries() {
    const startIndex = (this.currentPage - 1) * this.entriesPerPage;

    return this.filteredEntries.slice(
      startIndex,
      startIndex + this.entriesPerPage,
    );
  }

  /**
   * Filters the lp entries based on the checked filters.
   * If a query is present, it will also filter the entries based on the query.
   */
  @action filterEntries = () => {
    this._filteredEntries = FiltersStore.getFilteredEntries<LearningPath>(
      this.entries,
      this.getFilterConfigs(),
      this.filters,
    );

    if (this.query) {
      this.searchEntries(this.query);
      this._filteredEntries = this.searchResults;
    }
  };

  @action getLearningPaths = async (force?: boolean) => {
    if (this.learningPaths.length && !force) {
      return this.learningPaths;
    }
    try {
      this.loading = true;

      const learningPaths = await getLearningPaths();

      if (learningPaths) {
        this.learningPaths = learningPaths;
      }
    } catch {
      this.learningPaths = [];
    } finally {
      this.loading = false;
      this.loaded = true;
    }
    return this.learningPaths;
  };

  @action getLearningPathsForOrgLeaders = async (force?: boolean) => {
    if (this.learningPaths.length && !force) {
      return this.learningPaths;
    }
    try {
      this.loading = true;

      const learningPaths = await getLearningPathsForOrgLeader();

      if (learningPaths) {
        this.learningPaths = learningPaths;
      }
    } catch {
      this.learningPaths = [];
    } finally {
      this.loading = false;
      this.loaded = true;
    }
    return this.learningPaths;
  };

  /**
   * load the filters for the lp entries. This is used to populate the filters in the UI.
   * This loads the vocabulary data for each filter type and then renders the filters based on
   * what is available in the the lp entries. This ensures that filters only show up if they
   * exist in at least one lp entry.
   */
  @action getFilterInfo = async (
    entryList: Array<LearningPath> = this.entries || [],
  ) =>
    this.__getFilterInfo(
      this.rootStore.uiStore.currentLanguage,
      this.rootStore.vocabularyStore,
      entryList,
    );

  @action setCurrentPage = (page = 1) => {
    this._currentPage = page;
  };

  /**
   * Recalculates sidebar filter counts for skills paths.
   * Uses {@link FiltersStore.recalculateFilterCountsFromEntries},
   * applying the current search query when one is active.
   */
  @action recalculateFilterCounts = () => {
    this.recalculateFilterCountsFromEntries(this.entries, (entries) => {
      if (!this.query) {
        return entries;
      }

      const options = {
        shouldSort: true,
        threshold: 0,
        tokenize: true,
        matchAllTokens: true,
        maxPatternLength: 32,
        minMatchCharLength: 1,
        keys: [
          'title',
          'description.body',
          'prerequisites.body',
          'lp_elements.code',
          'lp_elements.title',
          'code',
          'categories',
        ],
      };

      return new Fuse(entries, options).search(this.query);
    });
  };

  @computed get joinedLearningPaths() {
    return this.learningPaths
      ?.filter((path) => path.joined === true)
      .sort(
        (pathA, pathB) =>
          new Date(pathB?.joined_at).getTime() -
          new Date(pathA?.joined_at).getTime(),
      );
  }

  @action joinLearningPath(code: string) {
    joinLearningPath(code).then(() => {
      const index = this.learningPaths.findIndex((p) => p.code === code);
      const pathToUpdate = this.learningPaths[index];
      pathToUpdate.joined = !pathToUpdate.joined;

      this.filterEntries();
    });
  }

  @action unjoinLearningPath(code: string) {
    unjoinLearningPath(code).then(() => {
      const index = this.learningPaths.findIndex((p) => p.code === code);
      const pathToUpdate = this.learningPaths[index];
      pathToUpdate.joined = !pathToUpdate.joined;

      this.filterEntries();
    });
  }

  @action clearPaths() {
    this.learningPaths = [];
  }

  @action async handleDeleteCSK(
    i18n,
    vocabularyStore,
    userStore,
    pathToDelete,
  ) {
    try {
      await deleteCustomSkillPath(pathToDelete.code);

      await this.init(userStore, true);
    } catch {
      throw new Error('Cannot delete a path');
    }
  }
}

export default LearningPathsStore;
